import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import { FaPlus, FaTrash, FaEdit, FaSave, FaTimes, FaWallet, FaChartLine, FaUniversity, FaMoneyBillWave, FaCalendarAlt, FaSearch } from 'react-icons/fa';
import { finances } from '../../services/api';
import Swal from 'sweetalert2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import moment from 'moment';

// Register ChartJS components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend);

const defaultTransaction = {
  description: '',
  cash: 0,
  onHandCash: 0,
  cards: 0,
  wireTransfer: 0,
  compensatedCash: 0,
  compensatedCard: 0
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0 }
};

const SummaryCard = ({ title, value, icon: Icon, color }) => (
  <motion.div
    variants={itemVariants}
    className={`bg-white rounded-xl shadow-lg p-6 ${color} transition-transform duration-300 hover:scale-105`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-bold mt-2">€{value.toFixed(2)}</p>
      </div>
      <div className={`${color.replace('border-l-4', 'bg-opacity-10')} p-4 rounded-full`}>
        <Icon className={`${color.replace('border-l-4', 'text-opacity-80')} w-6 h-6`} />
      </div>
    </div>
  </motion.div>
);

const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-6 py-3 font-medium rounded-lg transition-all duration-300 ${
      active
        ? 'bg-blue-600 text-white shadow-lg'
        : 'bg-white text-gray-600 hover:bg-gray-50'
    }`}
  >
    {children}
  </button>
);

const FinanceManagement = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [transactions, setTransactions] = useState({ expenses: [], earnings: [] });
  const [allTransactions, setAllTransactions] = useState({ expenses: [], earnings: [] });
  const [banks, setBanks] = useState([]);
  const [funds, setFunds] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [newTransaction, setNewTransaction] = useState({ ...defaultTransaction });
  const [transactionType, setTransactionType] = useState('expense');
  const [editingBank, setEditingBank] = useState(null);
  const [editingFund, setEditingFund] = useState(null);
  const [activeTab, setActiveTab] = useState('transactions');
  const [historicalData, setHistoricalData] = useState({
    weekly: { labels: [], expenses: [], earnings: [] },
    monthly: { labels: [], expenses: [], earnings: [] },
    yearly: { labels: [], expenses: [], earnings: [] }
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');
  const [newBank, setNewBank] = useState({ name: '', available: 0, pending: 0 });
  const [newFund, setNewFund] = useState({ type: 'funds', available: 0, pending: 0 });
  const [showNewBankForm, setShowNewBankForm] = useState(false);
  const [showNewFundForm, setShowNewFundForm] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      // Calculate date ranges
      const startDate = moment(selectedDate).startOf('day').toISOString();
      const endDate = moment(selectedDate).endOf('day').toISOString();
      
      // For historical data and global totals, we need all transactions
      const historicalStartDate = moment(selectedDate).subtract(11, 'months').startOf('month').toISOString();
      const historicalEndDate = moment(selectedDate).endOf('month').toISOString();

      const [transactionsRes, summaryRes, banksRes, fundsRes, historicalTransactionsRes, allTransactionsRes] = await Promise.all([
        finances.getTransactions({ startDate, endDate }),
        finances.getSummary({ startDate, endDate }),
        finances.getBanks(),
        finances.getFunds(),
        finances.getTransactions({ startDate: historicalStartDate, endDate: historicalEndDate }),
        finances.getTransactions({}) // Get all transactions without date filter
      ]);

      const dayTransactions = transactionsRes.data;
      const historicalTransactions = historicalTransactionsRes.data;
      const globalTransactions = allTransactionsRes.data;
      
      const expenses = dayTransactions.filter(t => t.type === 'expense');
      const earnings = dayTransactions.filter(t => t.type === 'earning');
      
      // Set daily transactions
      setTransactions({ expenses, earnings });
      
      // Set all transactions for global totals
      setAllTransactions({
        expenses: globalTransactions.filter(t => t.type === 'expense'),
        earnings: globalTransactions.filter(t => t.type === 'earning')
      });

      setSummary(summaryRes.data);
      setBanks(banksRes.data);
      setFunds(fundsRes.data);

      // Process historical data using the selected date as reference
      const weeklyData = processWeeklyData(historicalTransactions, selectedDate);
      const monthlyData = processMonthlyData(historicalTransactions, selectedDate);

      setHistoricalData({
        weekly: weeklyData,
        monthly: monthlyData,
        yearly: {
          labels: monthlyData.labels.slice(-12),
          expenses: monthlyData.expenses.slice(-12),
          earnings: monthlyData.earnings.slice(-12)
        }
      });
    } catch (error) {
      console.error('Error fetching financial data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch financial data'
      });
    }
  };

  const processWeeklyData = (transactions, selectedDate) => {
    const startOfWeek = moment(selectedDate).startOf('week');
    
    const days = Array.from({ length: 7 }, (_, i) => 
      moment(startOfWeek).add(i, 'days').format('ddd'));
    
    const data = days.map((day, index) => {
      const dayDate = moment(startOfWeek).add(index, 'days');
      const dayTransactions = transactions.filter(t => 
        moment(t.date).isSame(dayDate, 'day')
      );
      
      const expenses = dayTransactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => {
          const total = (t.cash || 0) + (t.onHandCash || 0) + (t.cards || 0) + (t.wireTransfer || 0);
          return sum + (isNaN(total) ? 0 : total);
        }, 0);

      const earnings = dayTransactions
        .filter(t => t.type === 'earning')
        .reduce((sum, t) => {
          const total = (t.cash || 0) + (t.onHandCash || 0) + (t.cards || 0) + 
                       (t.wireTransfer || 0) + (t.compensatedCash || 0) + (t.compensatedCard || 0);
          return sum + (isNaN(total) ? 0 : total);
        }, 0);

      return { expenses, earnings };
    });

    return {
      labels: days,
      expenses: data.map(d => d.expenses || 0),
      earnings: data.map(d => d.earnings || 0)
    };
  };

  const processMonthlyData = (transactions, selectedDate) => {
    const startOfMonth = moment(selectedDate).startOf('month');
    const daysInMonth = moment(selectedDate).daysInMonth();
    
    const days = Array.from({ length: daysInMonth }, (_, i) => 
      moment(startOfMonth).add(i, 'days').format('D'));
    
    const data = days.map(day => {
      const dayTransactions = transactions.filter(t => 
        moment(t.date).format('D') === day && 
        moment(t.date).isSame(moment(selectedDate), 'month')
      );
      
      const expenses = dayTransactions
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => {
          const total = (t.cash || 0) + (t.onHandCash || 0) + (t.cards || 0) + (t.wireTransfer || 0);
          return sum + (isNaN(total) ? 0 : total);
        }, 0);

      const earnings = dayTransactions
        .filter(t => t.type === 'earning')
        .reduce((sum, t) => {
          const total = (t.cash || 0) + (t.onHandCash || 0) + (t.cards || 0) + 
                       (t.wireTransfer || 0) + (t.compensatedCash || 0) + (t.compensatedCard || 0);
          return sum + (isNaN(total) ? 0 : total);
        }, 0);

      return { expenses, earnings };
    });

    return {
      labels: days,
      expenses: data.map(d => d.expenses || 0),
      earnings: data.map(d => d.earnings || 0)
    };
  };

  const calculateTotals = () => {
    const totalExpenses = allTransactions.expenses.reduce((sum, transaction) => sum + transaction.amount, 0);
    const totalEarnings = allTransactions.earnings.reduce((sum, transaction) => sum + transaction.amount, 0);
    
    return {
      expenses: totalExpenses,
      earnings: totalEarnings,
      balance: totalEarnings - totalExpenses
    };
  };

  const getTableTitle = (type) => {
    const currentDate = moment(selectedDate).format('MMMM D, YYYY');
    return `${type} for ${currentDate}`;
  };

  // Chart options and configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `€${value}`
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      }
    }
  };

  const handleAddTransaction = async () => {
    try {
      await finances.createTransaction({
        ...newTransaction,
        type: transactionType,
        date: selectedDate,
        paymentMethod: selectedPaymentMethod,
        paid: false
      });
      setNewTransaction({ ...defaultTransaction });
      await fetchData();
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Transaction added successfully'
      });
    } catch (error) {
      console.error('Error adding transaction:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add transaction'
      });
    }
  };

  const handleUpdateTransaction = async (id) => {
    try {
      await finances.updateTransaction(id, editingTransaction);
      setEditingTransaction(null);
      await fetchData();
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Transaction updated successfully'
      });
    } catch (error) {
      console.error('Error updating transaction:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update transaction'
      });
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        await finances.deleteTransaction(id);
        await fetchData();
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Transaction deleted successfully'
        });
      }
    } catch (error) {
      console.error('Error deleting transaction:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete transaction'
      });
    }
  };

  const handleUpdateBank = async (id) => {
    try {
      await finances.updateBank(id, editingBank);
      setEditingBank(null);
      fetchData();
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Bank balance updated successfully'
      });
    } catch (error) {
      console.error('Error updating bank:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update bank balance'
      });
    }
  };

  const handleUpdateFund = async (id) => {
    try {
      await finances.updateFund(id, editingFund);
      setEditingFund(null);
      fetchData();
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Fund balance updated successfully'
      });
    } catch (error) {
      console.error('Error updating fund:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update fund balance'
      });
    }
  };

  const handleCreateBank = async () => {
    try {
      await finances.createBank(newBank);
      setNewBank({ name: '', available: 0, pending: 0 });
      setShowNewBankForm(false);
      await fetchData();
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Bank added successfully'
      });
    } catch (error) {
      console.error('Error creating bank:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add bank'
      });
    }
  };

  const handleDeleteBank = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        await finances.deleteBank(id);
        await fetchData();
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Bank deleted successfully'
        });
      }
    } catch (error) {
      console.error('Error deleting bank:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete bank'
      });
    }
  };

  const handleCreateFund = async () => {
    try {
      await finances.createFund(newFund);
      setNewFund({ type: 'funds', available: 0, pending: 0 });
      setShowNewFundForm(false);
      await fetchData();
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Fund added successfully'
      });
    } catch (error) {
      console.error('Error creating fund:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add fund'
      });
    }
  };

  const handleDeleteFund = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        await finances.deleteFund(id);
        await fetchData();
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Fund deleted successfully'
        });
      }
    } catch (error) {
      console.error('Error deleting fund:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete fund'
      });
    }
  };

  const handleNewTransactionChange = (field, value) => {
    const updatedTransaction = { ...newTransaction };
    if (field === 'description') {
      updatedTransaction[field] = value;
    } else if (field === 'amount') {
      updatedTransaction.amount = parseFloat(value) || 0;
      // For earnings, set the selected payment method value
      if (transactionType === 'earning') {
        // Reset all payment methods first
        ['cash', 'onHandCash', 'cards', 'wireTransfer', 'compensatedCash', 'compensatedCard'].forEach(method => {
          updatedTransaction[method] = 0;
        });
        // Set the selected payment method
        updatedTransaction[selectedPaymentMethod] = parseFloat(value) || 0;
      }
    }
    setNewTransaction(updatedTransaction);
  };

  const handleUpdatePaidStatus = async (transactionId, paid) => {
    try {
      await finances.updateTransaction(transactionId, { paid });
      await fetchData();
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Transaction marked as ${paid ? 'paid' : 'unpaid'}`
      });
    } catch (error) {
      console.error('Error updating payment status:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update payment status'
      });
    }
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };
  

  const getFilteredTransactions = (transactions) => {
    if (!searchQuery) return transactions;
    return transactions.filter(t => 
      t.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Calculate summary values
  const totalEarnings = transactions.earnings.reduce((sum, t) => {
    const total = (t.cash || 0) + (t.onHandCash || 0) + (t.cards || 0) + 
                 (t.wireTransfer || 0) + (t.compensatedCash || 0) + (t.compensatedCard || 0);
    return sum + (isNaN(total) ? 0 : total);
  }, 0);

  const totalExpenses = transactions.expenses.reduce((sum, t) => {
    const total = (t.cash || 0) + (t.onHandCash || 0) + (t.cards || 0) + (t.wireTransfer || 0);
    return sum + (isNaN(total) ? 0 : total);
  }, 0);

  const totalBankBalance = banks.reduce((sum, b) => sum + b.available, 0);
  const totalFundsBalance = funds.reduce((sum, f) => sum + f.available, 0);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-gray-50 p-6 space-y-6"
    >
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <DatePicker
              selected={selectedDate}
              onChange={setSelectedDate}
              dateFormat="dd/MM/yyyy"
              className="w-full border rounded-lg px-4 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <FaCalendarAlt className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <select
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
            className="border rounded-lg px-4 py-2 bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="expense">Add Expense</option>
            <option value="earning">Add Earning</option>
          </select>
        </div>
        {activeTab === 'transactions' && (
          <div className="relative w-1/2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search transactions..."
              className="w-full border rounded-lg px-4 py-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        )}
        <div className="flex space-x-4">
          <TabButton
            active={activeTab === 'transactions'}
            onClick={() => setActiveTab('transactions')}
          >
            Transactions
          </TabButton>
          <TabButton
            active={activeTab === 'accounts'}
            onClick={() => setActiveTab('accounts')}
          >
            Accounts
          </TabButton>
        </div>
      </div>

      {/* Summary Cards */}
      <motion.div
        variants={containerVariants}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <SummaryCard
          title="Total Earnings"
          value={totalEarnings}
          icon={FaChartLine}
          color="border-l-4 border-green-500"
        />
        <SummaryCard
          title="Total Expenses"
          value={totalExpenses}
          icon={FaMoneyBillWave}
          color="border-l-4 border-red-500"
        />
        <SummaryCard
          title="Bank Balance"
          value={totalBankBalance}
          icon={FaUniversity}
          color="border-l-4 border-blue-500"
        />
        <SummaryCard
          title="Available Funds"
          value={totalFundsBalance}
          icon={FaWallet}
          color="border-l-4 border-purple-500"
        />
      </motion.div>

      {/* Main Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'transactions' ? (
          <motion.div
            key="transactions"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="space-y-6"
          >
            {/* Expenses Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-red-100 text-red-600">
                    <FaMoneyBillWave />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{getTableTitle('Expenses')}</h3>
                </div>
                <div className="text-sm text-gray-500">
                  {getFilteredTransactions(transactions.expenses).length} {getFilteredTransactions(transactions.expenses).length === 1 ? 'transaction' : 'transactions'}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {getFilteredTransactions(transactions.expenses).map((transaction) => (
                      <tr 
                        key={transaction._id} 
                        className={`hover:bg-gray-50 ${!transaction.paid && transaction.type === 'expense' ? 'bg-red-50' : ''}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingTransaction?._id === transaction._id ? (
                            <input
                              type="text"
                              value={editingTransaction.description}
                              onChange={(e) => setEditingTransaction({
                                ...editingTransaction,
                                description: e.target.value
                              })}
                              className="w-full border rounded px-2 py-1"
                            />
                          ) : (
                            transaction.description
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingTransaction?._id === transaction._id ? (
                            <select
                              value={editingTransaction.paymentMethod}
                              onChange={(e) => {
                                const newMethod = e.target.value;
                                const amount = editingTransaction.amount;
                                setEditingTransaction({
                                  ...editingTransaction,
                                  paymentMethod: newMethod,
                                  amount: amount,
                                  cash: 0,
                                  onHandCash: 0,
                                  cards: 0,
                                  wireTransfer: 0,
                                  compensatedCash: 0,
                                  compensatedCard: 0,
                                  [newMethod]: amount
                                });
                              }}
                              className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="onHandCash">On-hand Cash</option>
                              <option value="cards">Cards</option>
                              <option value="wireTransfer">Wire Transfer</option>
                              <option value="compensatedCash">Compensated Cash</option>
                              <option value="compensatedCard">Compensated Card</option>
                            </select>
                          ) : (
                            capitalizeFirstLetter(transaction.paymentMethod?.replace(/([A-Z])/g, ' $1').toLowerCase() || '')
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingTransaction?._id === transaction._id ? (
                            <input
                              type="number"
                              value={editingTransaction.amount}
                              onChange={(e) => {
                                const amount = parseFloat(e.target.value) || 0;
                                setEditingTransaction({
                                  ...editingTransaction,
                                  amount,
                                  [editingTransaction.paymentMethod]: amount
                                });
                              }}
                              className="w-full border rounded px-2 py-1"
                            />
                          ) : (
                            `€${transaction.amount.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleUpdatePaidStatus(transaction._id, !transaction.paid)}
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              transaction.paid
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {transaction.paid ? '✓ Paid' : '✕ Not Paid'}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {editingTransaction?._id === transaction._id ? (
                            <>
                              <button onClick={() => handleUpdateTransaction(transaction._id)} className="text-green-600 hover:text-green-900 mr-2">
                                <FaSave className="w-4 h-4" />
                              </button>
                              <button onClick={() => setEditingTransaction(null)} className="text-gray-600 hover:text-gray-900">
                                <FaTimes className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => setEditingTransaction(transaction)} className="text-blue-600 hover:text-blue-900 mr-2">
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDeleteTransaction(transaction._id)} className="text-red-600 hover:text-red-900">
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                    {transactionType === 'expense' && (
                      <tr className="bg-blue-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="text"
                            value={newTransaction.description}
                            onChange={(e) => handleNewTransactionChange('description', e.target.value)}
                            placeholder="Description"
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={selectedPaymentMethod}
                            onChange={(e) => {
                              setSelectedPaymentMethod(e.target.value);
                              handleNewTransactionChange('amount', newTransaction.amount, e.target.value);
                            }}
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="onHandCash">On-hand Cash</option>
                            <option value="cards">Cards</option>
                            <option value="wireTransfer">Wire Transfer</option>
                            <option value="compensatedCash">Compensated Cash</option>
                            <option value="compensatedCard">Compensated Card</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            value={newTransaction.amount}
                            onChange={(e) => handleNewTransactionChange('amount', e.target.value)}
                            placeholder="0.00"
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td colSpan="2" className="px-6 py-4 whitespace-nowrap text-right">
                          <button onClick={handleAddTransaction} className="text-green-600 hover:text-green-900">
                            <FaPlus className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-semibold">Total</td>
                      <td></td>
                      <td className="px-6 py-4 whitespace-nowrap font-semibold">
                        €{transactions.expenses.reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Earnings Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-green-100 text-green-600">
                    <FaChartLine />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{getTableTitle('Earnings')}</h3>
                </div>
                <div className="text-sm text-gray-500">
                  {getFilteredTransactions(transactions.earnings).length} {getFilteredTransactions(transactions.earnings).length === 1 ? 'transaction' : 'transactions'}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {getFilteredTransactions(transactions.earnings).map((transaction) => (
                      <tr key={transaction._id} className={`hover:bg-gray-50 ${!transaction.paid ? 'bg-red-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingTransaction?._id === transaction._id ? (
                            <input
                              type="text"
                              value={editingTransaction.description}
                              onChange={(e) => setEditingTransaction({
                                ...editingTransaction,
                                description: e.target.value
                              })}
                              className="w-full border rounded px-2 py-1"
                            />
                          ) : (
                            transaction.description
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingTransaction?._id === transaction._id ? (
                            <select
                              value={(() => {
                                // Find the payment method with non-zero value
                                const method = ['cash', 'onHandCash', 'cards', 'wireTransfer', 'compensatedCash', 'compensatedCard']
                                  .find(m => editingTransaction[m] > 0);
                                return method || 'onHandCash';
                              })()}
                              onChange={(e) => {
                                const newMethod = e.target.value;
                                const amount = Object.entries(editingTransaction)
                                  .find(([key, value]) => ['cash', 'onHandCash', 'cards', 'wireTransfer', 'compensatedCash', 'compensatedCard']
                                    .includes(key) && value > 0)?.[1] || 0;
                                
                                const updatedTransaction = {
                                  ...editingTransaction,
                                  cash: 0,
                                  onHandCash: 0,
                                  cards: 0,
                                  wireTransfer: 0,
                                  compensatedCash: 0,
                                  compensatedCard: 0,
                                  [newMethod]: amount
                                };
                                setEditingTransaction(updatedTransaction);
                              }}
                              className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="onHandCash">On-hand Cash</option>
                              <option value="cash">Cash</option>
                              <option value="cards">Cards</option>
                              <option value="wireTransfer">Wire Transfer</option>
                              <option value="compensatedCash">Compensated Cash</option>
                              <option value="compensatedCard">Compensated Card</option>
                            </select>
                          ) : (
                            (() => {
                              const method = ['cash', 'onHandCash', 'cards', 'wireTransfer', 'compensatedCash', 'compensatedCard']
                                .find(m => transaction[m] > 0);
                              return capitalizeFirstLetter(method?.replace(/([A-Z])/g, ' $1').toLowerCase() || '');
                            })()
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingTransaction?._id === transaction._id ? (
                            <input
                              type="number"
                              value={(() => {
                                const amount = Object.entries(editingTransaction)
                                  .find(([key, value]) => ['cash', 'onHandCash', 'cards', 'wireTransfer', 'compensatedCash', 'compensatedCard']
                                    .includes(key) && value > 0)?.[1] || 0;
                                return amount;
                              })()}
                              onChange={(e) => {
                                const amount = parseFloat(e.target.value) || 0;
                                const method = ['cash', 'onHandCash', 'cards', 'wireTransfer', 'compensatedCash', 'compensatedCard']
                                  .find(m => editingTransaction[m] > 0) || 'onHandCash';
                                
                                const updatedTransaction = {
                                  ...editingTransaction,
                                  cash: 0,
                                  onHandCash: 0,
                                  cards: 0,
                                  wireTransfer: 0,
                                  compensatedCash: 0,
                                  compensatedCard: 0,
                                  [method]: amount
                                };
                                setEditingTransaction(updatedTransaction);
                              }}
                              className="w-full border rounded px-2 py-1"
                            />
                          ) : (
                            `€${Object.entries(transaction)
                              .find(([key, value]) => ['cash', 'onHandCash', 'cards', 'wireTransfer', 'compensatedCash', 'compensatedCard']
                                .includes(key) && value > 0)?.[1].toFixed(2) || '0.00'}`
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={() => handleUpdatePaidStatus(transaction._id, !transaction.paid)}
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              transaction.paid
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {transaction.paid ? '✓ Paid' : '✕ Not Paid'}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {editingTransaction?._id === transaction._id ? (
                            <>
                              <button onClick={() => handleUpdateTransaction(transaction._id)} className="text-green-600 hover:text-green-900 mr-2">
                                <FaSave className="w-4 h-4" />
                              </button>
                              <button onClick={() => setEditingTransaction(null)} className="text-gray-600 hover:text-gray-900">
                                <FaTimes className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => setEditingTransaction(transaction)} className="text-blue-600 hover:text-blue-900 mr-2">
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDeleteTransaction(transaction._id)} className="text-red-600 hover:text-red-900">
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                    {transactionType === 'earning' && (
                      <tr className="bg-blue-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="text"
                            value={newTransaction.description}
                            onChange={(e) => handleNewTransactionChange('description', e.target.value)}
                            placeholder="Description"
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={selectedPaymentMethod}
                            onChange={(e) => {
                              setSelectedPaymentMethod(e.target.value);
                              // Transfer the current amount to the new payment method
                              const currentAmount = newTransaction[selectedPaymentMethod];
                              handleNewTransactionChange('amount', currentAmount);
                            }}
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="onHandCash">On-hand Cash</option>
                            <option value="cash">Cash</option>
                            <option value="cards">Cards</option>
                            <option value="wireTransfer">Wire Transfer</option>
                            <option value="compensatedCash">Compensated Cash</option>
                            <option value="compensatedCard">Compensated Card</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            value={newTransaction[selectedPaymentMethod]}
                            onChange={(e) => handleNewTransactionChange('amount', e.target.value)}
                            placeholder="0.00"
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button onClick={handleAddTransaction} className="text-green-600 hover:text-green-900">
                            <FaPlus className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-semibold">Total</td>
                      <td></td>
                      <td className="px-6 py-4 whitespace-nowrap font-semibold">
                        €{transactions.earnings.reduce((sum, t) => 
                          sum + Object.entries(t)
                            .filter(([key]) => ['cash', 'onHandCash', 'cards', 'wireTransfer', 'compensatedCash', 'compensatedCard'].includes(key))
                            .reduce((methodSum, [, value]) => methodSum + value, 0)
                        , 0).toFixed(2)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Financial Charts Section */}
            <div className="mt-8 space-y-8">
              {/* Weekly Overview */}
              <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Overview ({moment(selectedDate).format('MMM D')} - {moment(selectedDate).endOf('week').format('MMM D, YYYY')})</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Weekly Summary</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Total Earnings</p>
                        <p className="text-lg font-semibold text-green-600">
                          €{historicalData.weekly.earnings.reduce((a, b) => a + b, 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Expenses</p>
                        <p className="text-lg font-semibold text-red-600">
                          €{historicalData.weekly.expenses.reduce((a, b) => a + b, 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Net Balance</p>
                        <p className="text-lg font-semibold text-blue-600">
                          €{(historicalData.weekly.earnings.reduce((a, b) => a + b, 0) - historicalData.weekly.expenses.reduce((a, b) => a + b, 0)).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Daily Average</p>
                        <p className="text-lg font-semibold text-purple-600">
                          €{((historicalData.weekly.earnings.reduce((a, b) => a + b, 0) - historicalData.weekly.expenses.reduce((a, b) => a + b, 0)) / 7).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Day-by-Day Breakdown</h4>
                    <div className="space-y-2">
                      {historicalData.weekly.labels.map((day, index) => (
                        <div key={day} className="flex justify-between items-center">
                          <span className="text-sm text-gray-500">{day}</span>
                          <div className="flex items-center space-x-4">
                            <span className="text-sm text-green-600">+€{historicalData.weekly.earnings[index].toFixed(2)}</span>
                            <span className="text-sm text-red-600">-€{historicalData.weekly.expenses[index].toFixed(2)}</span>
                            <span className={`text-sm ${historicalData.weekly.earnings[index] - historicalData.weekly.expenses[index] >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              €{(historicalData.weekly.earnings[index] - historicalData.weekly.expenses[index]).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="h-80">
                  <Line
                    data={{
                      labels: historicalData.weekly.labels,
                      datasets: [
                        {
                          label: 'Expenses',
                          data: historicalData.weekly.expenses,
                          borderColor: 'rgb(239, 68, 68)',
                          backgroundColor: 'rgba(239, 68, 68, 0.5)',
                          tension: 0.3
                        },
                        {
                          label: 'Earnings',
                          data: historicalData.weekly.earnings,
                          borderColor: 'rgb(34, 197, 94)',
                          backgroundColor: 'rgba(34, 197, 94, 0.5)',
                          tension: 0.3
                        }
                      ]
                    }}
                    options={chartOptions}
                  />
                </div>
              </motion.div>

              {/* Monthly Overview */}
              <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Overview ({moment(selectedDate).format('MMMM YYYY')})</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Monthly Summary</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Total Earnings</p>
                        <p className="text-lg font-semibold text-green-600">
                          €{historicalData.monthly.earnings.reduce((a, b) => a + b, 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Expenses</p>
                        <p className="text-lg font-semibold text-red-600">
                          €{historicalData.monthly.expenses.reduce((a, b) => a + b, 0).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Net Balance</p>
                        <p className="text-lg font-semibold text-blue-600">
                          €{(historicalData.monthly.earnings.reduce((a, b) => a + b, 0) - historicalData.monthly.expenses.reduce((a, b) => a + b, 0)).toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Daily Average</p>
                        <p className="text-lg font-semibold text-purple-600">
                          €{((historicalData.monthly.earnings.reduce((a, b) => a + b, 0) - historicalData.monthly.expenses.reduce((a, b) => a + b, 0)) / historicalData.monthly.labels.length).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Weekly Averages</h4>
                    <div className="space-y-2">
                      {Array.from({ length: Math.ceil(historicalData.monthly.labels.length / 7) }).map((_, weekIndex) => {
                        const weekStart = weekIndex * 7;
                        const weekEnd = Math.min(weekStart + 7, historicalData.monthly.labels.length);
                        const weekEarnings = historicalData.monthly.earnings.slice(weekStart, weekEnd).reduce((a, b) => a + b, 0);
                        const weekExpenses = historicalData.monthly.expenses.slice(weekStart, weekEnd).reduce((a, b) => a + b, 0);
                        return (
                          <div key={weekIndex} className="flex justify-between items-center">
                            <span className="text-sm text-gray-500">Week {weekIndex + 1}</span>
                            <div className="flex items-center space-x-4">
                              <span className="text-sm text-green-600">+€{weekEarnings.toFixed(2)}</span>
                              <span className="text-sm text-red-600">-€{weekExpenses.toFixed(2)}</span>
                              <span className={`text-sm ${weekEarnings - weekExpenses >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                €{(weekEarnings - weekExpenses).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="h-80">
                  <Bar
                    data={{
                      labels: historicalData.monthly.labels,
                      datasets: [
                        {
                          label: 'Expenses',
                          data: historicalData.monthly.expenses,
                          backgroundColor: 'rgba(239, 68, 68, 0.7)'
                        },
                        {
                          label: 'Earnings',
                          data: historicalData.monthly.earnings,
                          backgroundColor: 'rgba(34, 197, 94, 0.7)'
                        }
                      ]
                    }}
                    options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        tooltip: {
                          ...chartOptions.plugins?.tooltip,
                          callbacks: {
                            label: function(context) {
                              const label = context.dataset.label || '';
                              const value = context.parsed.y;
                              const day = historicalData.monthly.labels[context.dataIndex];
                              return `${label} (Day ${day}): €${value.toFixed(2)}`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </motion.div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="accounts"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {/* Banks Table */}
            <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                  <FaUniversity />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Banks</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bank
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Available
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pending
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {banks.map((bank) => (
                      <tr key={bank._id}>
                        <td className="px-6 py-4 whitespace-nowrap">{bank.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingBank?._id === bank._id ? (
                            <input
                              type="number"
                              value={editingBank.available}
                              onChange={(e) =>
                                setEditingBank({
                                  ...editingBank,
                                  available: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-full border rounded px-2 py-1"
                            />
                          ) : (
                            `€${bank.available.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingBank?._id === bank._id ? (
                            <input
                              type="number"
                              value={editingBank.pending}
                              onChange={(e) =>
                                setEditingBank({
                                  ...editingBank,
                                  pending: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-full border rounded px-2 py-1"
                            />
                          ) : (
                            `€${bank.pending.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {editingBank?._id === bank._id ? (
                            <>
                              <button
                                onClick={() => handleUpdateBank(bank._id)}
                                className="text-green-600 hover:text-green-900 mr-2"
                              >
                                <FaSave className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setEditingBank(null)}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <FaTimes className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => setEditingBank(bank)}
                                className="text-blue-600 hover:text-blue-900 mr-2"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteBank(bank._id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                    {showNewBankForm && (
                      <tr className="bg-blue-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="text"
                            value={newBank.name}
                            onChange={(e) =>
                              setNewBank({ ...newBank, name: e.target.value })
                            }
                            placeholder="Bank Name"
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            value={newBank.available}
                            onChange={(e) =>
                              setNewBank({
                                ...newBank,
                                available: parseFloat(e.target.value) || 0,
                              })
                            }
                            placeholder="Available"
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            value={newBank.pending}
                            onChange={(e) =>
                              setNewBank({
                                ...newBank,
                                pending: parseFloat(e.target.value) || 0,
                              })
                            }
                            placeholder="Pending"
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={handleCreateBank}
                            className="text-green-600 hover:text-green-900 mr-2"
                          >
                            <FaSave className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setShowNewBankForm(false)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <FaTimes className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="4" className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => setShowNewBankForm(true)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <FaPlus className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </motion.div>

            {/* Funds Table */}
            <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                  <FaWallet />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Funds</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Available
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pending
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {funds.map((fund) => (
                      <tr key={fund._id}>
                        <td className="px-6 py-4 whitespace-nowrap capitalize">
                          {fund.type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingFund?._id === fund._id ? (
                            <input
                              type="number"
                              value={editingFund.available}
                              onChange={(e) =>
                                setEditingFund({
                                  ...editingFund,
                                  available: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-full border rounded px-2 py-1"
                            />
                          ) : (
                            `€${fund.available.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {editingFund?._id === fund._id ? (
                            <input
                              type="number"
                              value={editingFund.pending}
                              onChange={(e) =>
                                setEditingFund({
                                  ...editingFund,
                                  pending: parseFloat(e.target.value) || 0,
                                })
                              }
                              className="w-full border rounded px-2 py-1"
                            />
                          ) : (
                            `€${fund.pending.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          {editingFund?._id === fund._id ? (
                            <>
                              <button
                                onClick={() => handleUpdateFund(fund._id)}
                                className="text-green-600 hover:text-green-900 mr-2"
                              >
                                <FaSave className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setEditingFund(null)}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <FaTimes className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => setEditingFund(fund)}
                                className="text-blue-600 hover:text-blue-900 mr-2"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteFund(fund._id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                    {showNewFundForm && (
                      <tr className="bg-blue-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={newFund.type}
                            onChange={(e) =>
                              setNewFund({ ...newFund, type: e.target.value })
                            }
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="funds">Funds</option>
                            <option value="cash">Cash</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            value={newFund.available}
                            onChange={(e) =>
                              setNewFund({
                                ...newFund,
                                available: parseFloat(e.target.value) || 0,
                              })
                            }
                            placeholder="Available"
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            value={newFund.pending}
                            onChange={(e) =>
                              setNewFund({
                                ...newFund,
                                pending: parseFloat(e.target.value) || 0,
                              })
                            }
                            placeholder="Pending"
                            className="w-full border rounded-lg px-3 py-2 bg-white/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            onClick={handleCreateFund}
                            className="text-green-600 hover:text-green-900 mr-2"
                          >
                            <FaSave className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setShowNewFundForm(false)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            <FaTimes className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="4" className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => setShowNewFundForm(true)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <FaPlus className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FinanceManagement;